/**
 * RejectIntakeModal — small Radix dialog to reject an Oscar intake with a required reason.
 *
 * Posts POST /api/projects/{jiraKey}/reject via useRejectIntake. Reason must be at least 10
 * characters. On success it toasts "Intake rejected", clears the form and closes; the intake is
 * removed from the list by react-query invalidation in the hook.
 */
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

import type { OscarIntake } from '@/types/intake';
import { useRejectIntake } from '@/hooks/useIntakes';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

const MIN_REASON_LENGTH = 10;

export interface RejectIntakeModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  intake: OscarIntake | null;
}

export function RejectIntakeModal({
  open,
  onOpenChange,
  intake,
}: RejectIntakeModalProps): JSX.Element {
  const reject = useRejectIntake(intake?.jira_key ?? '');
  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);

  // Reset the form whenever a different intake is targeted or the dialog reopens.
  useEffect(() => {
    if (open) {
      setReason('');
      setTouched(false);
    }
  }, [open, intake]);

  const trimmed = reason.trim();
  const tooShort = trimmed.length < MIN_REASON_LENGTH;

  const handleReject = async (): Promise<void> => {
    setTouched(true);
    if (!intake || tooShort) return;
    try {
      await reject.mutateAsync({ reason: trimmed });
      toast.success('Intake rejected');
      onOpenChange(false);
    } catch {
      toast.error('Failed to reject intake');
    }
  };

  const title = intake?.company_name
    ? `${intake.company_name}${intake.deal_name ? ` — ${intake.deal_name}` : ''}`
    : (intake?.title ?? 'this intake');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Reject Intake</DialogTitle>
          <DialogDescription>
            Rejecting “{title}”. Provide a reason (minimum {MIN_REASON_LENGTH} characters). This is
            recorded and a Slack notification is posted.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 py-1">
          <Label htmlFor="reject-intake-reason">
            Reason <span className="text-error">*</span>
          </Label>
          <Textarea
            id="reject-intake-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            onBlur={() => setTouched(true)}
            placeholder="e.g. Not a delivery engagement / duplicate of an existing project"
            rows={4}
            aria-required="true"
            aria-invalid={touched && tooShort}
            aria-describedby="reject-intake-reason-help"
          />
          <p
            id="reject-intake-reason-help"
            className={
              touched && tooShort ? 'text-xs text-error' : 'text-xs text-text-secondary'
            }
          >
            {touched && tooShort
              ? `Please enter at least ${MIN_REASON_LENGTH} characters (${trimmed.length}/${MIN_REASON_LENGTH}).`
              : `${trimmed.length} characters`}
          </p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={reject.isPending}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleReject}
            disabled={reject.isPending || tooShort}
          >
            {reject.isPending && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
            {reject.isPending ? 'Rejecting…' : 'Reject Intake'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default RejectIntakeModal;
